<template>
  <div class="app-activation-bookable">
    <slot name="txtBefore"></slot>
    <div class="before-personal-data"><slot name="txtBeforePersonalDataContent"></slot></div>
    <div class="summary-row row">
      <ShowPersonalData :customer="getCustomer" class="summary-content">
        <template v-for="slot in possibleChildSlotsFor('PersonalData')" v-slot:[`${slot.child}`]>
          <slot :name="`${slot.parent}`"></slot>
        </template>
      </ShowPersonalData>
      <div class="before-payment-data"><slot name="txtBeforePaymentDataContent"></slot></div>
      <PaymentInformation :payment="_payment" v-if="hasTopUp">
        <template v-for="slot in possibleChildSlotsFor('Payment')" v-slot:[`${slot.child}`]>
          <slot :name="`${slot.parent}`"></slot>
        </template>
      </PaymentInformation>
      <div class="before-ident-data"><slot name="txtBeforeIdentDataContent"></slot></div>
      <PersonalDataIdentSummary>
        <template v-for="slot in possibleChildSlotsFor('IdentSummary')" v-slot:[`${slot.child}`]>
          <slot :name="`${slot.parent}`"></slot>
        </template>
      </PersonalDataIdentSummary>
      <div class="before-topup-data"><slot name="txtBeforeTopUpDataContent"></slot></div>
      <TopUpSummaryWrapper :change-btn="false" v-if="hasTopUp">
        <template v-for="slot in possibleChildSlotsFor('TopUp')" v-slot:[`${slot.child}`]>
          <slot :name="`${slot.parent}`"></slot>
        </template>
      </TopUpSummaryWrapper>
    </div>
    <ListOptionsDetails :showSummaryText="false" :item-list="itemList" :show-summary="true" v-if="showOptionsList">
      <template v-for="slot in possibleChildSlotsFor('ListOptions')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </ListOptionsDetails>
    <ConsentWrapper v-if="!isSuccessStep"
      :consentAGB.sync="consents.agb"
      :consentInformationArticle.sync="consents.informationArticle"
      :selectedInfoExchangeConsent.sync="consents.exchange"
      :selectedContactConsent.sync="consents.contact"
      :contractTermsVersion.sync="consents.contractTermsVersion"
    >
      <template v-for="slot in possibleChildSlotsFor('Consent')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </ConsentWrapper>
    <Stepper v-if="!isSuccessStep"
      @next="next()"
      :show-prev="false"
      :loadingNext="loading"
    >
      <template v-for="slot in possibleChildSlotsFor('Stepper')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </Stepper>
    <slot name="txtAfter"></slot>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import processSteps from '@/mixins/processSteps'
import Stepper from '@/components/generic/Stepper.vue'
import slotProxy from '@/mixins/slotProxy'
import PersonalDataIdentSummary from '@/components/process/FYVE/Activation/Summary/PersonalDataIdentSummary.vue'
import ShowPersonalData from '@/components/process/generic/PersonalData/ShowPersonalData.vue'
import PaymentInformation from './PaymentInformation.vue'
import TopUpSummaryWrapper from '@/components/process/FYVE/Activation/Wrapper/TopUpSummaryWrapper.vue'
import ConsentWrapper from '@/components/process/FYVE/Activation/Wrapper/ConsentWrapper.vue'
import ListOptionsDetails from '@/components/process/generic/Tariff/fyve/ListOptionsDetails.vue'

export const stepName = 'summary'

export default {
  name: 'ActivationSummary',
  props: {
    extra: {
      type: Array,
      default: () => {
        return []
      }
    }
  },
  data () {
    return {
      loading: false,
      consents: {
        exchange: false,
        contact: false,
        agb: true,
        contractTermsVersion: true
      }
    }
  },
  components: {
    Stepper,
    PersonalDataIdentSummary,
    ShowPersonalData,
    PaymentInformation,
    TopUpSummaryWrapper,
    ConsentWrapper,
    ListOptionsDetails
  },
  computed: {
    _payment: function () {
      return {
        ...this.payment,
        type: this.payment.paymentType
      }
    },
    isSuccessStep: function () {
      return this.showActivationView === 'success'
    },
    ...mapGetters('activation', [
      'showActivationView'
    ]),
    ...mapGetters('activation/topUp', [
      'hasTopUp', 'withoutTopUp'
    ]),
    ...mapGetters('activation/payment', [
      'payment'
    ]),
    ...mapGetters('activation/info', [
      'activatableTariffs'
    ]),
    ...mapGetters('activation/tariff', [
      'tariffId'
    ]),
    ...mapGetters('activation/tariffOptions', [
      'tariffOptionIds'
    ]),
    ...mapGetters('activation/personalData', [
      'customerCore'
    ]),
    getCustomer () {
      return {
        salutation: this.customerCore.gender,
        firstName: this.customerCore.firstName,
        lastName: this.customerCore.lastName,
        birthDate: this.customerCore.birthDate,
        email: this.customerCore.email,
        address: this.customerCore.address
      }
    },
    itemList: function () {
      const itemList = []
      // find tariff for list
      const chosenTariff = this.activatableTariffs.find(tar => {
        return parseInt(this.tariffId) === parseInt(tar.tariffId)
      })
      if (chosenTariff !== undefined) {
        const extraTariff = this.extra.find(elem => elem.tariffId === chosenTariff.tariffId)
        if (parseInt(this.tariffId) !== 92) {
          itemList.push({ ...chosenTariff, ...extraTariff })
        }

        // find tariff-options for list
        const possibleOptions = chosenTariff.allTariffOptions
        this.tariffOptionIds.forEach(opt => {
          const chosenTariffOption = possibleOptions.find(poss => {
            return poss.tariffoptionId === opt.tariffoptionId
          })
          if (chosenTariffOption !== undefined) {
            const chosenTariffOptionExtra = this.extra.find(poss => {
              return poss.tariffOptionId === opt.tariffoptionId
            })
            itemList.push({ ...chosenTariffOption, ...chosenTariffOptionExtra })
          }
        })
      }
      return itemList
    },
    showOptionsList: function () {
      return this.itemList.length !== 0
    }
  },
  methods: {
    ...mapActions('activation/submit', [
      'activationConfirm'
    ]),
    async next () {
      this.loading = true
      await this.activationConfirm({
        consents: this.consents,
        nextStep: this.nextStep
      })
      this.loading = false
    }
  },
  mixins: [
    processSteps(stepName, stepName, stepName),
    slotProxy()
  ]
}
</script>
